import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const userIcon = new L.Icon({
  iconUrl:
    'data:image/svg+xml;base64,' +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#5856a2" width="32" height="32">
      <circle cx="12" cy="12" r="8" fill="#5856a2" stroke="white" stroke-width="2"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

function MapController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 15);
  }, [center, map]);
  return null;
}

function NearbyStores({ onSelectStore, onBack }) {
  const [location, setLocation] = useState(null);
  const [nearbyStores, setNearbyStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  const requestLocation = () => {
    setHasRequested(true);
    setLoading(true);
    setError(null);
    setPermissionDenied(false);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ lat: latitude, lng: longitude });
        fetchNearbyStores(latitude, longitude);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setPermissionDenied(true);
          setError('Location access denied. Please enable location permissions.');
        } else {
          setError('Unable to retrieve your location. Please try again.');
        }
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const fetchNearbyStores = async (lat, lng) => {
    try {
      const radius = 1000;
      const query = `
        [out:json][timeout:25];
        (
          node["shop"](around:${radius},${lat},${lng});
          node["amenity"="restaurant"](around:${radius},${lat},${lng});
          node["amenity"="cafe"](around:${radius},${lat},${lng});
          node["amenity"="fast_food"](around:${radius},${lat},${lng});
          node["amenity"="fuel"](around:${radius},${lat},${lng});
        );
        out body;
      `;
      const response = await fetch(
        `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`
      );
      if (!response.ok) throw new Error('Failed to fetch nearby stores');
      const data = await response.json();
      const stores = data.elements
        .filter((el) => el.tags && el.tags.name)
        .map((el) => ({
          id: el.id,
          name: el.tags.name,
          type: el.tags.shop || el.tags.amenity || 'store',
          lat: el.lat,
          lng: el.lon,
          address: el.tags['addr:street']
            ? `${el.tags['addr:housenumber'] || ''} ${el.tags['addr:street']}`
            : null,
        }))
        .slice(0, 50);
      setNearbyStores(stores);
      setLoading(false);
    } catch {
      setError('Failed to load nearby stores. Please try again.');
      setLoading(false);
    }
  };

  const handleStoreClick = (store) => {
    if (onSelectStore) {
      onSelectStore({ name: store.name, category: store.type, location: store.address || 'Nearby' });
    }
  };

  const getStoreIcon = (type) => {
    if (type === 'restaurant' || type === 'fast_food') return 'restaurant';
    if (type === 'cafe') return 'coffee';
    if (type === 'fuel') return 'local_gas_station';
    if (type === 'supermarket') return 'shopping_cart';
    return 'storefront';
  };

  if (permissionDenied) {
    return (
      <div className="space-y-4">
        {onBack && (
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface">
            <span className="material-symbols-outlined text-lg">arrow_back</span> Back
          </button>
        )}
        <div className="flex flex-col items-center text-center py-10">
          <span className="material-symbols-outlined text-5xl text-on-surface-variant mb-3">location_off</span>
          <h3 className="text-lg font-semibold text-on-surface mb-2">Location Access Required</h3>
          <p className="text-sm text-on-surface-variant mb-4 max-w-xs">
            Enable location permissions in your browser settings to find nearby stores.
          </p>
          <button onClick={requestLocation}
            className="px-6 py-2.5 bg-primary text-on-primary rounded-full text-sm font-semibold">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (error && !permissionDenied) {
    return (
      <div className="space-y-4">
        {onBack && (
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface">
            <span className="material-symbols-outlined text-lg">arrow_back</span> Back
          </button>
        )}
        <div className="flex flex-col items-center text-center py-10">
          <p className="text-sm text-error mb-3">{error}</p>
          <button onClick={requestLocation}
            className="px-6 py-2.5 bg-primary text-on-primary rounded-full text-sm font-semibold">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!hasRequested || (loading && !location)) {
    return (
      <div className="space-y-4">
        {onBack && (
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface">
            <span className="material-symbols-outlined text-lg">arrow_back</span> Back
          </button>
        )}
        <div className="flex flex-col items-center text-center py-10">
          {loading ? (
            <>
              <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin mb-3" />
              <p className="text-sm text-on-surface-variant">Getting your location...</p>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-5xl text-primary mb-3">my_location</span>
              <h3 className="text-lg font-semibold text-on-surface mb-2">Find Nearby Stores</h3>
              <p className="text-sm text-on-surface-variant mb-4 max-w-xs">
                Discover stores around you and check which card gives you the best benefits
              </p>
              <button onClick={requestLocation}
                className="px-6 py-2.5 bg-primary text-on-primary rounded-full text-sm font-semibold">
                Share My Location
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface">
          <span className="material-symbols-outlined text-lg">arrow_back</span> Back
        </button>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-on-surface">Stores Near You</h3>
        <button onClick={requestLocation} className="text-sm text-primary font-medium flex items-center gap-1">
          <span className="material-symbols-outlined text-base">refresh</span> Refresh
        </button>
      </div>

      <div className="rounded-2xl overflow-hidden shadow-card">
        <MapContainer
          center={[location.lat, location.lng]}
          zoom={15}
          style={{ height: '300px', width: '100%' }}
        >
          <MapController center={[location.lat, location.lng]} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[location.lat, location.lng]} icon={userIcon}>
            <Popup>You are here</Popup>
          </Marker>
          {nearbyStores.map((store) => (
            <Marker key={store.id} position={[store.lat, store.lng]}>
              <Popup>
                <div className="text-center">
                  <strong className="text-sm">{store.name}</strong>
                  <p className="text-xs text-gray-500">{store.type}</p>
                  <button
                    className="mt-1 px-3 py-1 bg-[#5856a2] text-white text-xs rounded-full"
                    onClick={() => handleStoreClick(store)}
                  >
                    Check Benefits
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {loading && nearbyStores.length === 0 && (
        <div className="flex items-center justify-center gap-2 py-3">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <span className="text-sm text-on-surface-variant">Loading nearby stores...</span>
        </div>
      )}

      {nearbyStores.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-on-surface mb-3">
            Nearby Stores ({nearbyStores.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {nearbyStores.slice(0, 12).map((store) => (
              <button
                key={store.id}
                onClick={() => handleStoreClick(store)}
                className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-bento hover:shadow-bento-hover text-left transition-shadow"
              >
                <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>
                    {getStoreIcon(store.type)}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-on-surface truncate">{store.name}</p>
                  <p className="text-xs text-on-surface-variant capitalize">{store.type.replace('_', ' ')}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NearbyStores;
